import { useQuery, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import {
  GET_SALES_TARGET_CONFIG,
  GET_ALL_SALES_TARGET_OVERRIDES,
  GET_AMOUNT_FOR_DATE,
  GET_SALES_CALENDAR,
  UPDATE_DEFAULT_SALES_AMOUNT,
  CREATE_SALES_TARGET_OVERRIDE,
  UPDATE_SALES_TARGET_OVERRIDE,
  DELETE_SALES_TARGET_OVERRIDE,
} from '../graphql/salesTargetOperations';

export interface SalesTargetConfig {
  id: string;
  defaultAmount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface SalesTargetOverride {
  id: string;
  startDate: Date;
  endDate: Date;
  amount: number;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CalendarDay {
  date: Date;
  amount: number;
  isOverride: boolean;
  description?: string;
}

// Hook para obtener configuración de monto predeterminado
export const useSalesTargetConfig = () => {
  const { data, loading, error, refetch } = useQuery<{ getSalesTargetConfig: SalesTargetConfig }>(
    GET_SALES_TARGET_CONFIG,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    config: data?.getSalesTargetConfig,
    loading,
    error,
    refetch,
  };
};

// Hook para obtener todos los overrides (montos especiales por fecha)
export const useSalesTargetOverrides = () => {
  const { data, loading, error, refetch } = useQuery<{
    getAllSalesTargetOverrides: SalesTargetOverride[];
  }>(GET_ALL_SALES_TARGET_OVERRIDES, {
    fetchPolicy: 'cache-and-network',
  });

  return {
    overrides: data?.getAllSalesTargetOverrides || [],
    loading,
    error,
    refetch,
  };
};

// Hook para obtener monto para una fecha específica
export const useAmountForDate = (date: Date) => {
  const { data, loading, error } = useQuery(GET_AMOUNT_FOR_DATE, {
    variables: { date },
    fetchPolicy: 'cache-and-network',
  });

  return {
    amountData: data?.getAmountForDate,
    loading,
    error,
  };
};

// Hook para obtener calendario de montos
export const useSalesCalendar = (startDate: Date, endDate: Date) => {
  const { data, loading, error } = useQuery<{ getSalesCalendar: CalendarDay[] }>(
    GET_SALES_CALENDAR,
    {
      variables: { startDate, endDate },
      fetchPolicy: 'cache-and-network',
      skip: !startDate || !endDate,
    }
  );

  return {
    calendar: data?.getSalesCalendar || [],
    loading,
    error,
  };
};

// Hook para actualizar monto predeterminado
export const useUpdateDefaultAmount = () => {
  const [updateMutation, { loading }] = useMutation(UPDATE_DEFAULT_SALES_AMOUNT, {
    refetchQueries: [{ query: GET_SALES_TARGET_CONFIG }],
  });

  const updateDefaultAmount = useCallback(
    async (amount: number) => {
      try {
        const result = await updateMutation({
          variables: { amount },
        });
        return result.data?.updateDefaultSalesAmount;
      } catch (err) {
        console.error('Error updating default amount:', err);
        throw err;
      }
    },
    [updateMutation]
  );

  return {
    updateDefaultAmount,
    updating: loading,
  };
};

// Hook para crear override (monto especial por fechas)
export const useCreateOverride = () => {
  const [createMutation, { loading }] = useMutation(CREATE_SALES_TARGET_OVERRIDE, {
    refetchQueries: [{ query: GET_ALL_SALES_TARGET_OVERRIDES }],
  });

  const createOverride = useCallback(
    async (input: {
      startDate: Date;
      endDate: Date;
      amount: number;
      description?: string;
    }) => {
      try {
        const result = await createMutation({
          variables: { input },
        });
        return result.data?.createSalesTargetOverride;
      } catch (err) {
        console.error('Error creating override:', err);
        throw err;
      }
    },
    [createMutation]
  );

  return {
    createOverride,
    creating: loading,
  };
};

// Hook para actualizar override
export const useUpdateOverride = () => {
  const [updateMutation, { loading }] = useMutation(UPDATE_SALES_TARGET_OVERRIDE, {
    refetchQueries: [{ query: GET_ALL_SALES_TARGET_OVERRIDES }],
  });

  const updateOverride = useCallback(
    async (input: {
      id: string;
      startDate?: Date;
      endDate?: Date;
      amount?: number;
      description?: string;
    }) => {
      try {
        const result = await updateMutation({
          variables: { input },
        });
        return result.data?.updateSalesTargetOverride;
      } catch (err) {
        console.error('Error updating override:', err);
        throw err;
      }
    },
    [updateMutation]
  );

  return {
    updateOverride,
    updating: loading,
  };
};

// Hook para eliminar override
export const useDeleteOverride = () => {
  const [deleteMutation, { loading }] = useMutation(DELETE_SALES_TARGET_OVERRIDE, {
    refetchQueries: [{ query: GET_ALL_SALES_TARGET_OVERRIDES }],
  });

  const deleteOverride = useCallback(
    async (id: string) => {
      try {
        const result = await deleteMutation({
          variables: { id },
        });
        return result.data?.deleteSalesTargetOverride;
      } catch (err) {
        console.error('Error deleting override:', err);
        throw err;
      }
    },
    [deleteMutation]
  );

  return {
    deleteOverride,
    deleting: loading,
  };
};
